import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, IconButton, Fade, Popper } from '@mui/material';
import { Close as CloseIcon, TouchApp as TouchIcon } from '@mui/icons-material';

export const ViewSwitcherTutorial: React.FC = () => {
  const [show, setShow] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const shouldShow = localStorage.getItem('shouldShowViewSwitcherTutorial') === 'true';
    const hasShown = localStorage.getItem('hasShownViewSwitcherTutorial') === 'true';
    
    if (shouldShow && !hasShown) {
      // Wait for modals to close and page to fully render
      const timer = setTimeout(() => {
        // Check if any modals are still open
        const openDialogs = document.querySelectorAll('[role="dialog"]');
        if (openDialogs.length > 0) {
          // Modals still open, wait more
          const retryTimer = setTimeout(() => {
            const viewSwitcher = document.querySelector('[role="group"]') as HTMLElement;
            if (viewSwitcher) {
              setAnchorEl(viewSwitcher);
              setShow(true);
            }
          }, 1000);
          return () => clearTimeout(retryTimer);
        }
        
        // No modals open, show tutorial
        const viewSwitcher = document.querySelector('[role="group"]') as HTMLElement;
        if (viewSwitcher) {
          setAnchorEl(viewSwitcher);
          setShow(true);
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setShow(false);
    localStorage.setItem('hasShownViewSwitcherTutorial', 'true');
    localStorage.removeItem('shouldShowViewSwitcherTutorial');
  };

  if (!show || !anchorEl) return null;

  return (
    <Popper
      open={show}
      anchorEl={anchorEl}
      placement="bottom"
      transition
      style={{ zIndex: 1500 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={350}>
          <Paper
            elevation={8}
            sx={{
              mt: 1,
              p: 2,
              maxWidth: 320,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: '3px solid white',
              borderRadius: 2,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': {
                  boxShadow: '0 0 0 0 rgba(102, 126, 234, 0.7)',
                },
                '50%': {
                  boxShadow: '0 0 0 10px rgba(102, 126, 234, 0)',
                },
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TouchIcon sx={{ fontSize: 28, mt: 0.5, animation: 'bounce 1s infinite' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  💡 Quick Tip
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Switch between <strong>Developer</strong>, <strong>Studio</strong>, <strong>Chatbots</strong> and <strong>Chains</strong> anytime using these buttons. Studio is a ChatGPT-style chat plus App Builder.
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ 
                  color: 'white',
                  mt: -0.5,
                  mr: -0.5,
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

